import React, { FC, useEffect, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { ButtonOptions, Dialog } from '@modernloop/shared/components';
import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';

import DoNotShowAgainKey from './DoNotShowAgainKey';
import useDoNotShowAgain from './useDoNotShowAgain';

type Props = {
  children?: React.ReactNode;
  // Required Props
  /** Title of the dialog */
  title: string;
  /** Whether the dialog is open or not */
  isOpen: boolean;
  /** Text to be displayed in the confirmation button */
  confirmationText: string;
  /** Action to be performed when the user confirms */
  onConfirm: () => void;
  /** Text to be displayed in the cancellation button */
  cancellationText: string;
  /** Action to be performed when the user cancels */
  onCancel: () => void;
  /** Action to be performed when the user closes */
  onClose: () => void;

  // Optional Props
  /** Subtitle of the dialog */
  subtitle?: string;
  /** Key to store the "do not show again" preference */
  doNotShowAgainKey?: DoNotShowAgainKey;
  /** Whether the dialog is destructive or not */
  isDestructive?: boolean;
  /** Decoration element */
  decoration?: JSX.Element;
  /** data-testid for the confirmation button */
  confirmationDataTestId?: string;
  /** data-testid for the cancellation button */
  cancellationDataTestId?: string;
};

const DoNotShowAgainDialog: FC<Props> = ({
  title,
  subtitle,
  confirmationText,
  cancellationText,
  onClose,
  onCancel,
  onConfirm,
  doNotShowAgainKey,
  decoration,
  isOpen,
  isDestructive,
  confirmationDataTestId,
  cancellationDataTestId,
  children,
}) => {
  const { t } = useTranslation();
  const [isDoNotShowAgainChecked, setIsDoNotShowAgainChecked] = useState(false);
  const { isDoNotShowAgainLoading, doNotShowAgain, addToDoNotShowAgain } = useDoNotShowAgain(doNotShowAgainKey);

  const confirmOptions: ButtonOptions = {
    onClick: async () => {
      if (isDoNotShowAgainChecked) {
        await addToDoNotShowAgain();
        // Return early so we don't call `onConfirm` twice.
        // If we didn't have this return - we would call `onConfirm` right below and in the `useEffect` hook after the `doNotShowAgain` state is set to true.
        // Returning here allows the `useEffect` to run after the `doNotShowAgain` state is set to true and then call `onConfirm`.
        return;
      }

      onConfirm();
    },
    label: confirmationText,
    isDangerous: isDestructive,
    dataTestId: confirmationDataTestId ?? 'confirmation-dialog-confirm-button',
  };

  const cancelOptions: ButtonOptions = {
    onClick: onCancel,
    label: cancellationText,
    dataTestId: cancellationDataTestId ?? 'confirmation-dialog-cancel-button',
  };

  // If the user has checked the "do not show again" checkbox, we want to call the `onConfirm` function immediately.
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    if (doNotShowAgain) {
      onConfirm();
    }
  }, [doNotShowAgain, onConfirm]);

  if (isDoNotShowAgainLoading || doNotShowAgain) {
    return null;
  }

  return (
    <Dialog
      dataTestId="confirmation-dialog"
      decoration={decoration}
      title={title}
      subTitle={subtitle}
      open={isOpen}
      onClose={onClose}
      cancelOptions={cancelOptions}
      submitOptions={confirmOptions}
      maxWidth="xs"
    >
      <Stack spacing={2}>
        <Typography variant="body1">{children}</Typography>
        {doNotShowAgainKey && (
          <FormControlLabel
            style={{ marginLeft: '-12px' }}
            control={
              <Checkbox checked={isDoNotShowAgainChecked} onChange={() => setIsDoNotShowAgainChecked((a) => !a)} />
            }
            label={t('hiring-dashboard-common:common.dontShowAgain')}
          />
        )}
      </Stack>
    </Dialog>
  );
};

export default DoNotShowAgainDialog;
